// Xavier (successor to Scholar, recycled per DIR-084 — Informed Consent)
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'scholar_token_wallet_v1';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useTokenWallet — system-wide bearer-token wallet.
 *
 * Architect directive (2026-05-27): every visitor to Xavier carries a
 * token wallet. Wallet entries are bearer codes the visitor has redeemed
 * on this device, with their current balance. Shape is crypto-ready:
 * when the wallet rail moves to on-chain, each entry's `source` field
 * flips from `gumroad`/`code` to a wallet-address; the balance math
 * stays identical.
 *
 * localStorage-only for now (no auth). When auth ships, the wallet
 * will sync from the user's account record on first load.
 *
 * Entry shape:
 *   { code, value, balance, source, last_seen }
 * Aggregate `balance` is the sum across all entries.
 */
function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToStorage(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* quota or disabled — non-fatal */
  }
}

export default function useTokenWallet() {
  const [entries, setEntries] = useState(loadFromStorage);

  const totalBalance = entries.reduce((sum, e) => sum + (Number(e.balance) || 0), 0);

  // Listen for cross-tab updates (multi-window same browser)
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY) {
        setEntries(loadFromStorage());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /**
   * Record (or update) a code after a successful redeem. Called by
   * RedeemCodePanel — passes the bearer code and the server-reported
   * tokens_remaining after the spend.
   */
  const upsert = useCallback(({ code, value, balance, source = 'gumroad' }) => {
    if (!code) return;
    setEntries((prev) => {
      const filtered = prev.filter((e) => e.code !== code);
      const next = [
        {
          code,
          value: Number(value) || 0,
          balance: Number(balance) || 0,
          source,
          last_seen: new Date().toISOString(),
        },
        ...filtered,
      ];
      saveToStorage(next);
      return next;
    });
  }, []);

  /**
   * Refresh balance for one code (or all) by hitting /api/redeem/balance.
   * If a code was spent on another device, the wallet self-heals.
   */
  const refresh = useCallback(async (codeFilter = null) => {
    const targets = codeFilter ? entries.filter((e) => e.code === codeFilter) : entries;
    if (targets.length === 0) return;
    const results = await Promise.all(
      targets.map(async (e) => {
        try {
          const r = await fetch(
            `${API_URL}/api/redeem/balance/${encodeURIComponent(e.code)}`,
          );
          if (!r.ok) return { ...e, missing: true };
          const data = await r.json();
          return {
            ...e,
            value: Number(data.token_value) || e.value,
            balance: Number(data.tokens_remaining) || 0,
          };
        } catch {
          return e; // network error — keep stale value
        }
      }),
    );
    setEntries((prev) => {
      const map = new Map(prev.map((e) => [e.code, e]));
      results.forEach((r) => map.set(r.code, r));
      const next = Array.from(map.values()).filter((e) => !e.missing);
      saveToStorage(next);
      return next;
    });
  }, [entries]);

  const remove = useCallback((code) => {
    setEntries((prev) => {
      const next = prev.filter((e) => e.code !== code);
      saveToStorage(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setEntries([]);
    saveToStorage([]);
  }, []);

  return { entries, totalBalance, upsert, refresh, remove, clear };
}
